import { Box, Grid, InputLabel, OutlinedInput } from "@mui/material";
import { monoFontFamily } from "../theme";
import { useCallback, type FunctionComponent } from "react";
import MathLabel from "../components/MathLabel";
import type NodeData from "../features/NodeData";
import { formatNumber } from "../features/NumberFormatter";
import type OutputItemType from "../features/OutputItemType";

interface OutputItemProps {
  id: string;
  data: NodeData;
  itemHeight: number;
  inputWidth: number;
}

const OutputItems: FunctionComponent<OutputItemProps> = ({
  id,
  data,
  itemHeight,
  inputWidth,
}) => {
  const getOutputId = useCallback(
    (type: OutputItemType): string => {
      return `output-item-${id}-${type}`;
    },
    [id],
  );

  const visibleOutputItems = data.outputItems.filter((item) => {
    if (item.type === "value" && data.isNodeValuesVisible === false) {
      return false;
    }
    if (item.type === "derivative" && data.isNodeDerivativesVisible === false) {
      return false;
    }
    return true;
  });

  if (visibleOutputItems.length === 0) {
    return null;
  }

  return (
    <Grid container columnSpacing={1} wrap="nowrap">
      {/* Labels */}
      <Grid item xs>
        {visibleOutputItems.map((item) => (
          <Box
            key={item.type}
            display="flex"
            justifyContent="left"
            alignItems="center"
            height={itemHeight}
          >
            {/* Label */}
            <InputLabel
              data-testid={`label-${getOutputId(item.type)}`}
              htmlFor={getOutputId(item.type)}
            >
              <MathLabel
                parts={item.labelParts}
                onClickLatexLink={data.onDerivativeClick}
              />
            </InputLabel>
          </Box>
        ))}
      </Grid>

      {/* Outputs */}
      <Grid item xs>
        {visibleOutputItems.map((item) => (
          <Box
            key={item.type}
            display="flex"
            justifyContent="right"
            alignItems="center"
            height={itemHeight}
          >
            {/* Output */}
            <OutlinedInput
              id={getOutputId(item.type)}
              data-testid={getOutputId(item.type)}
              readOnly={true}
              // Keeps the layout stable while the step-by-step execution
              // hasn't revealed the value yet
              value={
                item.isHidden === true
                  ? "?"
                  : formatNumber(item.value, data.decimalPlaces ?? null)
              }
              size="small"
              sx={{
                backgroundColor: "action.hover",
                "& fieldset": { border: "none" },
              }}
              inputProps={{
                style: {
                  padding: "4px 8px",
                  textAlign: "right",
                  width: inputWidth,
                  fontFamily: monoFontFamily,
                  fontWeight: 600,
                  opacity: item.isHidden === true ? 0.4 : 1,
                  color:
                    item.type === "derivative"
                      ? data.isDarkMode
                        ? "#2dd4bf"
                        : "#0d9488"
                      : undefined,
                },
              }}
            ></OutlinedInput>
          </Box>
        ))}
      </Grid>
    </Grid>
  );
};

export default OutputItems;
